import { formatAreaWithReadableUnit } from 'src/app/services/helper.service';

export interface PredictionLegendForecast {
  hour: number;
  color: string;
  // Total area for this forecast hour, e.g. "400.00 sqm"; set only when feed_results.area has it.
  area?: string;
}

export interface PredictionLegendGroup {
  id: string;
  label: string;
  color: string;
  forecasts: PredictionLegendForecast[];
}

// Filter key for prediction feeds whose features carry no `class` (e.g. flood prediction).
export const UNCLASSIFIED_DETECTION = 'unclassified';

export function isUnclassifiedDetection(value: unknown): boolean {
  return value != null && String(value).trim() === UNCLASSIFIED_DETECTION;
}

function normalizedClass(value: unknown): string | null {
  if (value == null || String(value).trim() === '') return null;
  return String(value).trim();
}

function forecastHour(value: unknown): number | null {
  if (value == null || String(value).trim() === '') return null;
  const hour = Number(value);
  return Number.isFinite(hour) && hour !== 0 ? hour : null;
}

// Flood prediction sends feed_results.area = { total_area_<hour>_hour: number, metric: 'sqm' }.
function forecastArea(feedResults: any, hour: number): string | undefined {
  const value = feedResults?.area?.[`total_area_${hour}_hour`];
  if (value == null || String(value).trim() === '') return undefined;
  return formatAreaWithReadableUnit(value, feedResults.area.metric ?? 'sqm');
}

function compareClasses(left: string, right: string): number {
  const numericDifference = Number(left) - Number(right);
  return Number.isNaN(numericDifference)
    ? left.localeCompare(right)
    : numericDifference;
}

function values(value: unknown): unknown[] {
  return Array.isArray(value) ? value : value == null ? [] : [value];
}

export function getPredictionDetectionClasses(feedResults: any): string[] {
  if ((Array.isArray(feedResults?.detection_classes) && feedResults.detection_classes.length === 0) ||
      feedResults?.detection_class_count === 0) return [];

  const explicit = values(feedResults?.detection_classes)
    .map(normalizedClass).filter((value): value is string => value !== null);
  if (explicit.length) return [...new Set(explicit)].sort(compareClasses);

  // A count assumes the backend assigns sequential detection IDs 1..N.
  const count = Number(feedResults?.detection_count);
  if (feedResults?.detection_count != null && Number.isSafeInteger(count) && count >= 0 && count <= 1000) {
    return Array.from({ length: count }, (_, index) => String(index + 1));
  }
  return [];
}

export function getPredictionForecastHours(feedResults: any): number[] {
  return [...new Set(values(
    feedResults?.forecast_hours ?? feedResults?.forecast_h ?? feedResults?.forecast_hour
  ).map(forecastHour).filter((value): value is number => value !== null))]
    .sort((left, right) => left - right);
}

// The backend sends `feed_results: {}` when a run produced nothing; missing results prove nothing.
export function isEmptyPredictionFeedResult(feedResults: any): boolean {
  return feedResults != null && typeof feedResults === 'object' && !Array.isArray(feedResults) &&
    Object.keys(feedResults).length === 0;
}

/** Build the feed legend from information-service results, never viewport WFS features. */
export function buildPredictionLegendGroupsFromResults(
  feedResults: any,
  config: any,
  fallbackColor: string,
  idPrefix: string,
  selection?: { detectionClass?: string | number; forecastHour?: string | number | null }
): PredictionLegendGroup[] {
  if (!feedResults || typeof feedResults !== 'object') return [];

  const availableClasses = getPredictionDetectionClasses(feedResults);
  const hasNoDetectionClasses = (Array.isArray(feedResults.detection_classes) &&
    feedResults.detection_classes.length === 0) || feedResults.detection_class_count === 0;
  const selectedClass = isUnclassifiedDetection(selection?.detectionClass)
    ? null : normalizedClass(selection?.detectionClass);
  const classes = selectedClass
    ? availableClasses.filter((value) => value === selectedClass) : availableClasses;
  const availableHours = getPredictionForecastHours(feedResults);
  const selectedHour = forecastHour(selection?.forecastHour);
  const hours = selectedHour == null ? availableHours
    : availableHours.filter((hour) => hour === selectedHour);
  const groups = classes.map((detectionClass): PredictionLegendGroup => ({
      id: `${idPrefix}:detection:${detectionClass}`,
      label: availableClasses.length === 1 ? 'Detection' : `Detection ${detectionClass}`,
      color: config?.detectionColorMap?.[detectionClass]?.color ?? fallbackColor,
      forecasts: hours.map((hour) => ({
        hour,
        color: config?.classForecastColorMap?.[detectionClass]?.[hour]?.color ??
          config?.forecastColorMap?.[hour]?.color ?? fallbackColor,
        area: forecastArea(feedResults, hour),
      })),
    }));

  if (!selectedClass && (feedResults.has_unclassified_detection_polygons ||
      feedResults.has_unclassified_forecast_points ||
      (classes.length === 0 && hasNoDetectionClasses) ||
      (classes.length === 0 && hours.length > 0 && feedResults.detection_count == null))) {
    groups.push({
      id: `${idPrefix}:detection:unclassified`,
      label: classes.length === 0 && !feedResults.has_unclassified_detection_polygons &&
        !hasNoDetectionClasses
        ? 'Forecast hours' : 'Detection',
      color: fallbackColor,
      forecasts: feedResults.has_unclassified_forecast_points || classes.length === 0
        ? hours.map((hour) => ({
          hour,
          color: config?.forecastColorMap?.[hour]?.color ?? fallbackColor,
          area: forecastArea(feedResults, hour),
        })) : [],
    });
  }

  return groups;
}
